/**
 * Screener flow controller.
 *
 * Orchestrates the screener as a single React component so the
 * router can mount it under one route (`/screener/*`). The flow
 * has three phases driven by internal state:
 *
 *   - 'landing'  — `<ScreenerLanding />`, transitions to 'question'
 *   - 'question' — `<ScreenerQuestion />` for the next un-routed
 *                  question; auto-advances on every answer
 *   - 'outcome'  — one of the five outcome pages; supports
 *                  re-screen back to 'landing' and (for eligible)
 *                  hands off to the intake via `onEligibleContinue`
 *
 * Why state-machine inside one component instead of nested routes:
 *   - The flow is linear — we never need a deep-link to question N.
 *   - Browser-back inside the screener (history navigation) would
 *     fight our own back button. Keeping state internal makes the
 *     UX deterministic.
 *   - The integration test can drive the whole flow end-to-end
 *     without spinning up `<MemoryRouter>`.
 *
 * The router (Slice 6 wiring) mounts this component at `/`,
 * `/screener`, and `/screener/*` paths. The single component
 * decides which phase to render.
 */
import { useCallback, useState } from 'react';

import { ScreenerLanding } from './ScreenerLanding';
import { ScreenerQuestion } from './ScreenerQuestion';
import {
  EligibleOutcome,
  IneligibleCriminalOutcome,
  IneligibleRemovalOutcome,
  ReferAttorneyOutcome,
  ReferDifferentFormOutcome,
} from './ScreenerOutcomes';
import {
  routeScreener,
  type IScreenerAnswers,
  type ScreenerDecision,
} from './screenerRouter';
import {
  screenerQuestions,
  screenerQuestionsById,
  type ScreenerOutcomeId,
} from './screenerConfig';
import { saveScreenerHandoff } from './screenerHandoff';
import { reportEvent } from '../monitoring';

type Phase =
  | { kind: 'landing' }
  | {
      kind: 'question';
      questionId: string;
      /** Trail of question ids for the back button. */
      history: readonly string[];
    }
  | {
      kind: 'outcome';
      outcome: ScreenerOutcomeId;
      reason: string;
      /**
       * The question id whose answer fired this outcome. Carried so
       * the outcome page can offer a "Back to that question" affordance
       * — without it, a user who picks the wrong option mid-flow has
       * to wipe every answer via "Re-take screener" just to fix one
       * mistake. `undefined` when the outcome was reached at flow
       * start (no preceding question — currently never happens because
       * `routeScreener({})` returns `continue`, not an outcome).
       */
      previousQuestionId?: string;
      /** Question history preceding the outcome-firing answer. */
      previousHistory?: readonly string[];
    };

export interface IScreenerFlowProps {
  /**
   * Handler fired when the user clicks "Continue to full intake"
   * on the eligible outcome page. The router uses this to
   * navigate to `/sign-up` (or wherever the next-step entry
   * point is wired).
   */
  onEligibleContinue: () => void;
  /**
   * Fired when the user clicks the persistent "Already a member?
   * Sign in →" affordance in the top bar (rendered across every
   * screener surface). The router wires this to navigate('/sign-in')
   * so returning users don't have to walk through the screener
   * again just to find the auth entry.
   */
  onSignInClick?: () => void;
}

export function ScreenerFlow({
  onEligibleContinue,
  onSignInClick,
}: IScreenerFlowProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>({ kind: 'landing' });
  const [answers, setAnswers] = useState<IScreenerAnswers>({});

  const startScreener = useCallback(() => {
    reportEvent('screener_started');
    const decision = routeScreener({});
    if (decision.kind === 'continue') {
      setPhase({
        kind: 'question',
        questionId: decision.nextQuestionId,
        history: [],
      });
    } else {
      // Edge case — empty answer set already terminal. Shouldn't
      // happen with the current rule list but covered for safety.
      setPhase({
        kind: 'outcome',
        outcome: decision.outcome,
        reason: decision.reason,
      });
    }
  }, []);

  const applyAnswer = useCallback(
    (
      questionId: string,
      value: boolean | string | readonly string[],
    ): void => {
      const nextAnswers: IScreenerAnswers = {
        ...answers,
        [questionId]: value,
      };
      setAnswers(nextAnswers);
      reportEvent('screener_answered_question', {
        questionId,
        answerType: Array.isArray(value)
          ? 'multi_select'
          : typeof value === 'boolean'
            ? 'boolean'
            : 'single_select',
      });
      const decision: ScreenerDecision = routeScreener(nextAnswers);
      if (decision.kind === 'outcome') {
        if (decision.outcome === 'eligible') {
          // Cross-write the snapshot before transitioning so the
          // intake hydration sees it on the next mount.
          saveScreenerHandoff(nextAnswers);
        }
        reportEvent('screener_outcome', {
          outcomeId: decision.outcome,
          reason: decision.reason,
        });
        // Capture the trail leading up to this outcome so the outcome
        // page can offer "Back to that question" without dumping the
        // user at the landing page.
        setPhase(prev => {
          const previousHistory: readonly string[] =
            prev.kind === 'question' ? prev.history : [];
          return {
            kind: 'outcome',
            outcome: decision.outcome,
            reason: decision.reason,
            previousQuestionId: questionId,
            previousHistory,
          };
        });
        return;
      }
      // continue — push the current question onto history and advance.
      setPhase(prev => {
        if (prev.kind !== 'question') {
          return {
            kind: 'question',
            questionId: decision.nextQuestionId,
            history: [],
          };
        }
        return {
          kind: 'question',
          questionId: decision.nextQuestionId,
          history: [...prev.history, prev.questionId],
        };
      });
    },
    [answers],
  );

  const goBack = useCallback((): void => {
    setPhase(prev => {
      if (prev.kind !== 'question' || prev.history.length === 0) {
        return { kind: 'landing' };
      }
      const trimmed = prev.history.slice(0, -1);
      const previousId = prev.history[prev.history.length - 1];
      // Drop the last answer so the router doesn't immediately
      // re-route past the question the user wanted to revisit.
      setAnswers(current => {
        const { [prev.questionId]: _dropped, ...rest } =
          current as Record<string, unknown>;
        return rest as IScreenerAnswers;
      });
      return {
        kind: 'question',
        questionId: previousId,
        history: trimmed,
      };
    });
  }, []);

  const restart = useCallback((): void => {
    setAnswers({});
    setPhase({ kind: 'landing' });
  }, []);

  /**
   * From an outcome page, return to the question whose answer fired the
   * outcome with that answer cleared. The user can then pick a different
   * option instead of starting over via "Re-take screener".
   */
  const backToPreviousQuestion = useCallback((): void => {
    setPhase(prev => {
      if (prev.kind !== 'outcome' || !prev.previousQuestionId) {
        return prev;
      }
      const qid = prev.previousQuestionId;
      setAnswers(current => {
        const { [qid]: _dropped, ...rest } = current as Record<
          string,
          unknown
        >;
        return rest as IScreenerAnswers;
      });
      return {
        kind: 'question',
        questionId: qid,
        history: prev.previousHistory ?? [],
      };
    });
  }, []);

  if (phase.kind === 'landing') {
    return (
      <ScreenerLanding
        onStart={startScreener}
        onSignInClick={onSignInClick}
      />
    );
  }

  if (phase.kind === 'question') {
    const q = screenerQuestionsById.get(phase.questionId);
    if (!q) {
      // Defensive — config drift would land us here. Reset to
      // landing rather than render a blank page.
      return (
        <ScreenerLanding
          onStart={startScreener}
          onSignInClick={onSignInClick}
        />
      );
    }
    const index = screenerQuestions.findIndex(item => item.id === q.id);
    return (
      <ScreenerQuestion
        question={q}
        answers={answers}
        onAnswer={applyAnswer}
        onBack={phase.history.length === 0 ? undefined : goBack}
        index={index < 0 ? 0 : index}
        onSignInClick={onSignInClick}
      />
    );
  }

  // Outcome phase. Pass `onBack` whenever an outcome was reached via
  // a question answer (always true today) so the user can step back
  // and revise that one answer instead of restarting the whole flow.
  const onBack = phase.previousQuestionId
    ? backToPreviousQuestion
    : undefined;
  switch (phase.outcome) {
    case 'eligible':
      return (
        <EligibleOutcome
          reason={phase.reason}
          onContinue={onEligibleContinue}
          onRescreen={restart}
          onBack={onBack}
          onSignInClick={onSignInClick}
        />
      );
    case 'ineligible_criminal':
      return (
        <IneligibleCriminalOutcome
          reason={phase.reason}
          onRescreen={restart}
          onBack={onBack}
          onSignInClick={onSignInClick}
        />
      );
    case 'ineligible_removal':
      return (
        <IneligibleRemovalOutcome
          reason={phase.reason}
          onRescreen={restart}
          onBack={onBack}
          onSignInClick={onSignInClick}
        />
      );
    case 'refer_attorney':
      return (
        <ReferAttorneyOutcome
          reason={phase.reason}
          onRescreen={restart}
          onBack={onBack}
          onSignInClick={onSignInClick}
        />
      );
    case 'refer_different_form':
      return (
        <ReferDifferentFormOutcome
          reason={phase.reason}
          onRescreen={restart}
          onBack={onBack}
          onSignInClick={onSignInClick}
        />
      );
  }
}
